import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

export const RepeaterField = ({ field, value, onChange }) => {
  const rows = Array.isArray(value) ? value : [];
  const subFields = field.sub_fields || [];
  const updateRow = (idx, key, val) => { const u = [...rows]; u[idx] = { ...u[idx], [key]: val }; onChange(u); };
  return (
    <div className="space-y-2">
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
          <span className="text-xs text-slate-400 w-5">{idx + 1}.</span>
          {subFields.map(sf => (
            <Input key={sf.id} value={row[sf.id] || ""} onChange={(e) => updateRow(idx, sf.id, e.target.value)} placeholder={sf.label} className="text-sm" />
          ))}
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 shrink-0" onClick={() => onChange(rows.filter((_, i) => i !== idx))}><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...rows, {}])}><Plus className="w-3 h-3 mr-1" /> Add Row</Button>
    </div>
  );
};

// Renders every field type of a form (fill or edit). `data` is the values object.
export const FormFields = ({ fields = [], data = {}, onChange }) => (
  <div className="space-y-3">
    {fields.map(field => {
      const key = field.id || field.name;
      const set = (v) => onChange({ ...data, [key]: v });
      const val = data[key];
      if (field.type === "section_header") {
        return <div key={key} className="pt-4 pb-1 border-b border-slate-200 mt-2"><h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">{field.label}</h3></div>;
      }
      return (
        <div key={key} data-testid={`form-field-${key}`}>
          <Label>{field.label}{field.required && <span className="text-red-500 ml-1">*</span>}</Label>
          {field.type === "dropdown" ? (
            <select className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-white" value={val || ""} onChange={(e) => set(e.target.value)}>
              <option value="">Select...</option>
              {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          ) : field.type === "textarea" ? (
            <Textarea className="mt-1" value={val || ""} onChange={(e) => set(e.target.value)} rows={3} placeholder={field.placeholder || ""} />
          ) : field.type === "date" ? (
            <Input type="date" className="mt-1" value={val || ""} onChange={(e) => set(e.target.value)} />
          ) : field.type === "number" ? (
            <Input type="number" className="mt-1" value={val ?? ""} onChange={(e) => set(e.target.value)} placeholder={field.placeholder || ""} />
          ) : field.type === "checkbox" ? (
            <label className="flex items-center gap-2 mt-1 text-sm"><input type="checkbox" className="w-4 h-4 rounded" checked={!!val} onChange={(e) => set(e.target.checked)} /> {field.placeholder || "Yes"}</label>
          ) : field.type === "repeater" ? (
            <div className="mt-1"><RepeaterField field={field} value={val || []} onChange={set} /></div>
          ) : (
            <Input className="mt-1" value={val || ""} onChange={(e) => set(e.target.value)} placeholder={field.placeholder || ""} />
          )}
        </div>
      );
    })}
  </div>
);

// Read-only rendering of a submission, using the form definition for labels when available.
export const FormValues = ({ fields = [], data = {} }) => {
  const byKey = Object.fromEntries(fields.map(f => [f.id || f.name, f]));
  const keys = fields.length ? fields.map(f => f.id || f.name).filter(k => k in data || byKey[k]?.type === "section_header") : Object.keys(data);
  const fmt = (f, v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (Array.isArray(v)) {
      const sub = f?.sub_fields || [];
      return (
        <div className="space-y-1">
          {v.map((row, i) => <div key={i} className="text-xs bg-slate-50 rounded px-2 py-1">{sub.length ? sub.map(sf => `${sf.label}: ${row[sf.id] ?? "—"}`).join(" · ") : JSON.stringify(row)}</div>)}
        </div>
      );
    }
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  return (
    <div className="space-y-1">
      {keys.map(k => {
        const f = byKey[k];
        if (f?.type === "section_header") return <p key={k} className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">{f.label}</p>;
        return (
          <div key={k} className="flex justify-between gap-4 py-2 border-b border-slate-100">
            <span className="text-sm text-slate-500 shrink-0">{f?.label || k.replace(/_/g, " ")}</span>
            <span className="text-sm font-medium text-slate-800 text-right min-w-0">{fmt(f, data[k])}</span>
          </div>
        );
      })}
    </div>
  );
};
